import axios, { AxiosResponse } from 'axios';
import {
  ChangePasswordFormValues,
  CreateLocationRequest,
  ILoginFormData,
  IRegisterFormData,
  UpdateLocationRequest,
  UpdateUserRequest,
  UserRoleValue,
} from "./interfaces";
import {
  IApiPaginatedAccessLogsResponse,
  IApiAuthResponse,
  IApiLocationResponse,
  IApiLocationsResponse,
  IApiMessageResponse,
  IApiRegisterResponse,
  IApiSuccessResponse,
  IApiUserResponse,
  IApiUsersResponse,
} from './api-interfaces';

// const apiUrl = 'http://localhost:5000'
const apiUrl = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL : 'http://localhost:5000'
const normalizedApiUrl = String(apiUrl || '').replace(/\/+$/, '')
const apiBaseUrl = `${normalizedApiUrl}/api`
const apikey = process.env.REACT_APP_GOOGLE_API_KEY
const admiralsPlaceID = process.env.REACT_APP_ADMIRALS_PLACE_ID
const royalOakPlaceID = process.env.REACT_APP_ROYAL_OAK_PLACE_ID

axios.defaults.headers.common['Content-Type'] = 'application/json'
axios.defaults.withCredentials = true

function normalizeAuthResponse(response: AxiosResponse<IApiAuthResponse, any, {}>): AxiosResponse<IApiAuthResponse, any, {}> {
  return {
    ...response,
    data: {
      ...response.data,
      user: {
        ...response.data.user,
        firstName: response.data.user.firstName ?? null,
        lastName: response.data.user.lastName ?? null,
        location: response.data.user.location ?? null,
      },
    },
  };
}

export function getGoogleDetails(location: string):Promise<AxiosResponse<any, any, {}>> {
  // return axios.get(`https://maps.googleapis.com/maps/api/place/details/json?place_id=${admiralsPlaceID}&fields=name,rating,reviews&key=${apikey}`)
  return axios.get(`https://places.googleapis.com/v1/places/${admiralsPlaceID}?fields=name,rating,reviews&key=${apikey}`)
}

export function getGoogleReviews():Promise<Array<AxiosResponse<any, any, {}>>> {
  return Promise.all(
    [
      axios.get(`https://places.googleapis.com/v1/places/${admiralsPlaceID}?fields=name,rating,reviews&key=${apikey}`),
      axios.get(`https://places.googleapis.com/v1/places/${royalOakPlaceID}?fields=name,rating,reviews&key=${apikey}`),
    ]
  )
}

export function sendCateringRequest(values: any):Promise<AxiosResponse<IApiMessageResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/forms/catering`, values)
}

export function login(values: ILoginFormData):Promise<AxiosResponse<IApiAuthResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/login`, values).then(normalizeAuthResponse)
}

export function register(values: IRegisterFormData):Promise<AxiosResponse<IApiRegisterResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/register`, values)
}

export function resendVerification(email: string): Promise<AxiosResponse<IApiMessageResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/resend-verification`, { email })
}

export function forgotPassword(email: string): Promise<AxiosResponse<IApiMessageResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/forgot-password`, { email })
}

export function verifyEmail(token: string): Promise<AxiosResponse<IApiMessageResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/verify-email`, { token })
}

export function resetPassword(token: string, password: string): Promise<AxiosResponse<IApiMessageResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/reset-password`, { token, password })
}

export function logout(): Promise<AxiosResponse<IApiSuccessResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/logout`, {}, { withCredentials: true })
}

export function refreshAuthSession(): Promise<AxiosResponse<IApiAuthResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/refresh`, {}, { withCredentials: true }).then(normalizeAuthResponse)
}

export function getCurrentUser(): Promise<AxiosResponse<IApiAuthResponse, any, {}>> {
  return axios.get(`${apiBaseUrl}/auth/currentUser`, { withCredentials: true }).then(normalizeAuthResponse)
}

export function changePassword(values: Pick<ChangePasswordFormValues, 'currentPassword' | 'newPassword'>): Promise<AxiosResponse<IApiMessageResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/auth/change-password`, values, { withCredentials: true })
}

export function getAllUsers(): Promise<AxiosResponse<IApiUsersResponse, any, {}>> {
  return axios.get(`${apiBaseUrl}/users`, { withCredentials: true })
}

export function getAllLocations(): Promise<AxiosResponse<IApiLocationsResponse, any, {}>> {
  return axios.get(`${apiBaseUrl}/locations`, { withCredentials: true })
}

export function createLocation(values: CreateLocationRequest): Promise<AxiosResponse<IApiLocationResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/locations/create`, values, { withCredentials: true })
}

export function updateLocation(values: UpdateLocationRequest): Promise<AxiosResponse<IApiLocationResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/locations/update`, values, { withCredentials: true })
}

export function disableLocation(locationId: string): Promise<AxiosResponse<IApiLocationResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/locations/disable`, { id: locationId }, { withCredentials: true })
}

export function getPendingValidationUsers(): Promise<AxiosResponse<IApiUsersResponse, any, {}>> {
  return axios.get(`${apiBaseUrl}/users/pending-validation`, { withCredentials: true })
}

export function validateUser(userId: string): Promise<AxiosResponse<IApiUserResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/users/validate`, { id: userId }, { withCredentials: true })
}

export function disableUser(userId: string): Promise<AxiosResponse<IApiUserResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/users/disable`, { id: userId }, { withCredentials: true })
}

export function updateUser(values: UpdateUserRequest): Promise<AxiosResponse<IApiUserResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/users/update`, values, { withCredentials: true })
}

export function changeUserRole(userId: string, role: UserRoleValue): Promise<AxiosResponse<IApiUserResponse, any, {}>> {
  return axios.post(`${apiBaseUrl}/users/change-role`, { id: userId, role }, { withCredentials: true })
}

export function getAccessLogs(page: number, limit: number): Promise<AxiosResponse<IApiPaginatedAccessLogsResponse, any, {}>> {
  return axios.get(`${apiBaseUrl}/access-logs`, {
    withCredentials: true,
    params: {
      page,
      limit,
    },
  })
}